import { ReactElement, useEffect, useState } from 'react'
import { Box, Typography, CircularProgress, Alert } from '@mui/material'
import { MuiMarkdown } from 'mui-markdown'

import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { ShowNotes } from 'src/v3/core/types'
import {
  ShowNotesBlock,
  ShowNotesTextBlock,
  ShowNotesImageBlock,
  ShowNotesPDFBlock,
} from 'src/v3/components/show-notes-editor/types'
import { markdownStyles } from 'src/v3/utilities/markdown-styles'
import axios from 'src/utilities/http-client'
import { PDFViewer } from 'src/v3/components/show-notes-editor/components/pdf-viewer'
import {
  selectCurrentStep,
  selectStreamItems,
  stepCompleted,
} from '../classroom.slice'

function PDFBlockClassroom({ block }: { block: ShowNotesPDFBlock }) {
  let [presignedUrl, setPresignedUrl] = useState<string | null>(null)
  let [loadingUrl, setLoadingUrl] = useState<boolean>(true)
  let [urlError, setUrlError] = useState<Error | null>(null)

  useEffect(() => {
    let fetchPresignedUrl = async () => {
      try {
        setLoadingUrl(true)
        let response = await axios.get(
          `/api/classroom/show-notes/blocks/${block.id}/pdf`,
        )
        let url = response.data?.data || response.data
        setPresignedUrl(url)
        setUrlError(null)
      } catch (err: any) {
        setUrlError(err)
      } finally {
        setLoadingUrl(false)
      }
    }

    fetchPresignedUrl()
  }, [block.id])

  return (
    <Box sx={{ mb: 4 }}>
      <PDFViewer
        block={block}
        presignedUrl={presignedUrl}
        loading={loadingUrl}
        error={urlError}
      />
    </Box>
  )
}

export default (): ReactElement => {
  let dispatch = useAppDispatch()
  let streamItems = useAppSelector(selectStreamItems)
  let currentStep = useAppSelector(selectCurrentStep)
  let data = streamItems[currentStep] as ShowNotes

  let [blocks, setBlocks] = useState<ShowNotesBlock[]>([])
  let [loading, setLoading] = useState<boolean>(true)
  let [error, setError] = useState<string>('')

  // Fetch show notes blocks
  useEffect(() => {
    let fetchBlocks = async () => {
      if (!data?.id) return

      try {
        setLoading(true)
        let response = await axios.get(
          `/api/classroom/show-notes/${data.id}/blocks`,
        )
        let blocksData = response.data?.data || response.data || []

        // Handle wrapped format: [{ textBlock: {...} }, { imageBlock: {...} }, { pdfBlock: {...} }]
        let processedBlocks: ShowNotesBlock[] = blocksData
          .map((item: any) => {
            if (item.textBlock) {
              return {
                ...item.textBlock,
                blockType: 'text' as const,
              }
            }
            if (item.imageBlock) {
              return {
                ...item.imageBlock,
                blockType: 'image' as const,
              }
            }
            if (item.pdfBlock) {
              return {
                ...item.pdfBlock,
                blockType: 'pdf' as const,
              }
            }
            return null
          })
          .filter(Boolean)
          .toSorted(
            (a: ShowNotesBlock, b: ShowNotesBlock) =>
              (a.sequence || 0) - (b.sequence || 0),
          )

        setBlocks(processedBlocks)
        setError('')
      } catch (err: any) {
        setError(
          err?.response?.data || err?.message || 'Error loading show notes',
        )
      } finally {
        setLoading(false)
      }
    }

    fetchBlocks()
  }, [data?.id])

  useEffect(() => {
    // Mark step as completed when component mounts
    dispatch(stepCompleted())
  }, [dispatch])

  // Function to render individual blocks (same as preview modal)
  let renderBlock = (block: ShowNotesBlock) => {
    if (block.blockType === 'text') {
      let textBlock = block as ShowNotesTextBlock
      return (
        <Box key={block.id} sx={{ mb: 3 }}>
          <Box sx={markdownStyles}>
            <MuiMarkdown>{textBlock.markdown || ''}</MuiMarkdown>
          </Box>
        </Box>
      )
    }

    if (block.blockType === 'image') {
      let imageBlock = block as ShowNotesImageBlock
      if (!imageBlock.imageUrl) {
        return null // Don't render image blocks without images
      }

      return (
        <Box key={block.id} sx={{ mb: 4, textAlign: 'center' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mb: imageBlock.caption ? 2 : 0,
            }}
          >
            <img
              src={`${import.meta.env.VITE_CLOUDFRONT_URL}/images/${imageBlock.imageUrl.replace('originals', 'resized')}`}
              alt={
                imageBlock.altText || imageBlock.caption || 'Show notes image'
              }
              style={{
                width: `${imageBlock.widthPercent || 100}%`,
                maxWidth: '100%',
                height: 'auto',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              }}
            />
          </Box>
          {imageBlock.caption && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                fontStyle: 'italic',
                textAlign: 'center',
                maxWidth: '80%',
                mx: 'auto',
                lineHeight: 1.5,
              }}
            >
              {imageBlock.caption}
            </Typography>
          )}
        </Box>
      )
    }

    if (block.blockType === 'pdf') {
      let pdfBlock = block as ShowNotesPDFBlock
      if (!pdfBlock.pdfUrl) {
        return null // Don't render PDF blocks without PDFs
      }

      return <PDFBlockClassroom key={block.id} block={pdfBlock} />
    }

    return null
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    )
  }

  let hasContent = blocks.some(
    (block) =>
      (block.blockType === 'text' &&
        (block as ShowNotesTextBlock).markdown?.trim()) ||
      (block.blockType === 'image' &&
        (block as ShowNotesImageBlock).imageUrl) ||
      (block.blockType === 'pdf' && (block as ShowNotesPDFBlock).pdfUrl),
  )

  if (!hasContent) {
    return (
      <Alert severity="info" sx={{ mb: 3 }}>
        No content available for these show notes.
      </Alert>
    )
  }

  return (
    <Box
      sx={{
        maxWidth: '900px',
        mx: 'auto',
        '& > *:first-of-type': {
          mt: 0,
        },
        '& > *:last-child': {
          mb: 0,
        },
      }}
    >
      {blocks.map((block) => renderBlock(block))}
    </Box>
  )
}
